<template>
  <div class="account-container">
    <h1>Account</h1>
    
    <!-- Keyhole Section with Dynamic Button Behavior -->
    <div class="account-content">
      <!-- Keyhole Section -->
      <div class="">
        <!-- Keyhole Display with fixed dimensions -->
        <div v-if="userStats?.keyhole" class="keyhole-display center">
          <div class="keyhole-wrapper">
            <div class="keyhole-wrapper" v-html="userStats.keyhole"></div>
            <!-- Show download icon only when not already downloaded -->
            <img v-if="!isDownloaded" src="/assets/svgs/download.svg" class="regler-icon download-icon" alt="Download" @click="downloadKeyhole" />
          </div>
        </div>
        
        <div class="button-container">
          <!-- Show generate button when logged in via email AND no keyhole exists -->
          <button 
            v-if="isEmailLogin && !userStats?.keyhole" 
            @click="generateKeyhole" 
            class="action-button center"
            :disabled="isGenerating"
          >
            {{ buttonText }}
          </button>
          
          <!-- Show download button only when keyhole exists -->
          <button 
            v-if="isEmailLogin && userStats?.keyhole" 
            @click="downloadKeyhole" 
            class="action-button center"
            :disabled="isDownloaded"
          >
            {{ downloadButtonText }}
          </button>
        </div>
      </div>
      
      <!-- Level Section -->
      <div class="level-section">
        <h2>Level {{ userStats?.level }}</h2>
        <div class="level-progress">
          <div class="progress-bar">
            <div 
              class="progress-fill"
              :style="{ width: levelProgress + '%' }"
            ></div>
          </div>
          <span class="center progress-text">{{ ratingsToNextLevel }} Bewertungen bis Level {{ userStats?.level + 1 }}</span>
        </div>
      </div>
      
      <!-- Statistics Section -->
      <div class="stats-section">
        <!-- Ratings Stats -->
        <div class="stat-item">
          <div class="stat-display">
            <div class="stats-container ">
              <canvas ref="ratingsChart" class="stats-chart"></canvas>
            </div>
            <!-- For ratings -->
            <div class="slider-container">
              <div class="slider-track" ref="ratingSlider">
                <!-- Fill from right (0) to user's value -->
                <div class="slider-fill" :style="{ width: (userStats?.ratings.user / maxRatings) * 100 + '%', right: 0 }"></div>
                <!-- Thumb at user's position from right -->
                <div class="slider-thumb" :style="{ 
                  left: 100 - (userStats?.ratings.user / maxRatings) * 100 + '%',
                  right: 'auto'
                }"></div>
              </div>
            </div>
          </div>
          
          <h3 class="center"><span class="stat-value">{{ userStats?.ratings.user }} &nbsp;</span>  Laublöcher bewertet</h3>
        </div>
        
        <!-- Downloads Stats -->
        <div class="stat-item">
          <div class="stat-display">
            <div class="stats-container ">
              <canvas ref="downloadsChart" class="stats-chart"></canvas>
            </div>
            <!-- For downloads -->
            <div class="slider-container">
              <div class="slider-track" ref="downloadSlider">
                <!-- Fill from right (0) to user's value -->
                <div class="slider-fill" :style="{ width: (userStats?.downloads.user / maxDownloads) * 100 + '%', right: 0, left: 'auto' }"></div>
                <!-- Thumb at user's position from right -->
                <div class="slider-thumb" :style="{ right: (userStats?.downloads.user / maxDownloads) * 100 + '%' }"></div>
              </div>
            </div>
          </div>
      
          <h3 class="center">  <span class="stat-value">{{ userStats?.downloads.user }}</span>&nbsp; Laublöcher heruntergeladen</h3>
        </div>
      </div>
      
      <!-- Logout Button -->
      <div class="logout-section center">
        <button @click="logout" class="logout-button">Abmelden</button>
      </div>
    </div>

    <!-- DSGVO Links fixed bottom-right -->
    <div class="account-links">
      <a href="mailto:info@sonnenwirbel.com?subject=Account%20l%C3%B6schen%20%E2%80%93%20laubloch.de&body=Bitte%20l%C3%B6sche%20meinen%20Account.">Account löschen</a>
      <a href="mailto:info@sonnenwirbel.com?subject=Datenexport%20%E2%80%93%20laubloch.de&body=Bitte%20sende%20mir%20meine%20gespeicherten%20Daten.">Daten exportieren</a>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import Chart from 'chart.js/auto'
import { getUserStats, downloadNewKeyhole, logout as apiLogout } from '../services/api'
import '../styles/formenBewerter.css';

export default {
  name: 'AccountPage',
  
  setup() {
    const router = useRouter()
    const userStats = ref(null)
    const keyholeMessage = ref('')
    const ratingsChartInstance = ref(null)
    const downloadsChartInstance = ref(null)
    const ratingSlider = ref(null)
    const downloadSlider = ref(null)
    const ratingsChart = ref(null)
    const downloadsChart = ref(null)
    const isGenerating = ref(false)
    const isDownloaded = ref(false)
    const isEmailLogin = ref(false)
    const buttonText = ref('Schlüsselloch generieren')
    const downloadButtonText = ref('Schlüsselloch herunterladen')
    
    // Check if user is logged in via email
    onMounted(() => {
      const cookies = document.cookie.split(';')
      const loginMethod = cookies.find(cookie => cookie.trim().startsWith('login_method='))
      if (loginMethod && loginMethod.includes('email')) {
        isEmailLogin.value = true
      }
    })

    // Computed properties
    const maxRatings = computed(() => {
      if (!userStats.value?.ratings.all?.length) return 100
      
      // Sort all rating counts
      const sortedRatings = [...userStats.value.ratings.all.map(r => r.count)].sort((a, b) => a - b)
      
      // Use second highest value if available (exclude the highest outlier)
      if (sortedRatings.length > 1) {
        return sortedRatings[sortedRatings.length - 2]
      }
      
      // Otherwise use the highest/only value
      return sortedRatings[sortedRatings.length - 1]
    })
    
    const maxDownloads = computed(() => {
      if (!userStats.value?.downloads.all?.length) return 100
      return Math.max(...userStats.value.downloads.all.map(d => d.count))
    })

    const levelProgress = computed(() => {
      if (!userStats.value) return 0
      const levels = [0, 89, 144, 233, 377, 610, 987]
      const currentLevel = userStats.value.level
      const currentRatings = userStats.value.ratings.user
      
      if (currentLevel >= 6) return 100
      
      const currentThreshold = levels[currentLevel]
      const nextThreshold = levels[currentLevel + 1]
      return Math.min(100, Math.max(0, 
        ((currentRatings - currentThreshold) / (nextThreshold - currentThreshold)) * 100
      ))
    })

    const ratingsToNextLevel = computed(() => {
      if (!userStats.value) return 0
      const levels = [0, 89, 144, 233, 377, 610, 987]
      const currentLevel = userStats.value.level
      
      if (currentLevel >= 6) return 0
      
      const nextThreshold = levels[currentLevel + 1]
      return nextThreshold - userStats.value.ratings.user
    })

    const renderCharts = () => {
      if (!userStats.value) return

      renderStatsChart(
        ratingsChartInstance,
        ratingsChart.value,
        userStats.value.ratings.all,
        userStats.value.ratings.user
      )

      renderStatsChart(
        downloadsChartInstance,
        downloadsChart.value,
        userStats.value.downloads.all,
        userStats.value.downloads.user
      )
    }

    const renderStatsChart = (chartInstance, canvasRef, data, userValue) => {
      if (chartInstance.value) {
        chartInstance.value.destroy();
      }

      console.log('Rendering chart with data:', data);
      console.log('User value:', userValue);

      // Find the maximum value in the data
      // Handle empty data array case
      let dataValues = [];
      if (data && data.length > 0) {
        dataValues = data.map(stat => stat.count || 0).sort((a, b) => a - b);
      }
      
      // Remove the highest value to prevent outliers from skewing the scale
      let maxDataValue = 0;
      if (dataValues.length > 1) {
        // Remove the highest value and use the second highest
        maxDataValue = dataValues[dataValues.length - 2];
      } else if (dataValues.length === 1) {
        maxDataValue = dataValues[0];
      }
      
      // Compare with user value (but don't let user value be an outlier either)
      const maxValue = Math.max(maxDataValue, Math.min(userValue || 0, maxDataValue * 1.5));
      
      console.log('Data values:', dataValues);
      console.log('Excluding highest value:', dataValues.length > 0 ? dataValues[dataValues.length - 1] : 'none');
      console.log('Max data value (second highest):', maxDataValue);
      console.log('User value:', userValue);
      console.log('Max value for scaling:', maxValue);
      
      // Calculate a dynamic bin size based on max value
      const BIN_COUNT = 21;
      const BIN_SIZE = Math.ceil(maxValue / BIN_COUNT);
      
      // Create bins (0 to max)
      const bins = Array(BIN_COUNT).fill(0);

      // Populate bins
      data.forEach(stat => {
        const binIndex = Math.min(Math.floor(stat.count / BIN_SIZE), BIN_COUNT - 1);
        if (binIndex >= 0) {
          bins[binIndex]++;
        }
      });
      
      console.log('Bin size:', BIN_SIZE);
      console.log('Distribution of data in bins:', bins);

      // Create labels based on the dynamic bin size
      const labels = bins.map((_, i) => `${i * BIN_SIZE}`);

      const ctx = canvasRef.getContext('2d');
      chartInstance.value = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            data: bins,
            borderColor: 'black',
            borderWidth: 2,
            tension: 0.6,
            fill: true,
            backgroundColor: 'rgba(1, 1, 1, 1)',
            pointRadius: 0,
            spanGaps: false,
          }]
        },
        options: {
          clip: false,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { 
              display: false,
              reverse: true,
              position: 'top'
            },
            y: { 
              display: false 
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false }
          },
        }
      });
    };

    const processKeyholeForDisplay = (svgContent) => {
      if (!svgContent || !svgContent.includes('<svg')) return svgContent;
      
      // Ensure the SVG has the proper viewBox attribute
      let processedSvg = svgContent;
      
      if (!processedSvg.includes('viewBox')) {
        // Add a default viewBox if none exists
        processedSvg = processedSvg.replace(/<svg/, '<svg viewBox="0 0 500 500"');
      }
      
      // Remove any hardcoded width/height that could conflict with responsive sizing
      processedSvg = processedSvg
        .replace(/width="[^"]*"/g, '')
        .replace(/height="[^"]*"/g, '')
        .replace(/width='[^']*'/g, '')
        .replace(/height='[^']*'/g, '');
      
      return processedSvg;
    }

    const generateKeyhole = async () => {
      try {
        isGenerating.value = true
        buttonText.value = 'Warten...'
        
        const response = await downloadNewKeyhole()
        
        // Extract the SVG content regardless of error messages or warnings
        let svgContent = null
        
        if (typeof response === 'string') {
          // Handle string response that contains SVG
          const svgMatch = response.match(/<svg[\s\S]*?<\/svg>/i)
          if (svgMatch) svgContent = svgMatch[0]
        } else if (response.svg) {
          // Handle object response with svg property
          svgContent = response.svg
        } else if (typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'svg')) {
          // Handle JSON response with svg property
          svgContent = response.svg
        }
        
        if (svgContent && svgContent.includes('<svg')) {
          // Process SVG for proper display
          const processedSvg = processKeyholeForDisplay(svgContent);
          
          // Update stats if available
          if (response.stats) {
            userStats.value = {
              ...response.stats,
              keyhole: processedSvg
            };
          } else {
            // Just update the keyhole if stats not included
            userStats.value = {
              ...userStats.value,
              keyhole: processedSvg
            };
          }
          
          // Reset button state
          buttonText.value = 'Schlüsselloch generieren'
          
          isDownloaded.value = false
          
          renderCharts()
        } else {
          keyholeMessage.value = 'Fehler beim Generieren des Schlüssellochs.'
          buttonText.value = 'Schlüsselloch generieren'
        }
      } catch (error) {
        console.error('Failed to generate keyhole:', error)
        keyholeMessage.value = 'Fehler: ' + error.message
        buttonText.value = 'Schlüsselloch generieren'
      } finally {
        isGenerating.value = false
      }
    }

    const downloadKeyhole = async () => {
      try {
        if (!userStats.value?.keyhole) {
          keyholeMessage.value = 'Bitte zuerst ein Schlüsselloch generieren.'
          return
        }

        // Use original SVG for download, but ensure it has proper viewBox
        let downloadSvg = userStats.value.keyhole;
        
        // Make sure it has viewBox for download as well
        if (!downloadSvg.includes('viewBox')) {
          downloadSvg = downloadSvg.replace(/<svg/, '<svg viewBox="0 0 100 100"');
        }
        
        const blob = new Blob([downloadSvg], { type: 'image/svg+xml' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'keyhole.svg'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        
        downloadButtonText.value = 'Schlüsselloch gespeichert'
        isDownloaded.value = true
        
        setTimeout(() => {
          downloadButtonText.value = 'Schlüsselloch herunterladen'
          isDownloaded.value = false
        }, 3000)
      } catch (error) {
        keyholeMessage.value = 'Fehler beim Herunterladen: ' + error.message
        console.error('Failed to download keyhole:', error)
      }
    }

    const logout = async () => {
      try {
        await apiLogout()
        router.push('/login')
      } catch (error) {
        console.error('Failed to logout:', error)
        keyholeMessage.value = 'Fehler beim Abmelden: ' + error.message
      }
    }

    // Lifecycle hooks
    onMounted(async () => {
      try {
        // Check for login keyhole in localStorage first
        const loginKeyhole = localStorage.getItem('auth_keyhole');
        const stats = await getUserStats();
        
        if (loginKeyhole && loginKeyhole.includes('data-keyhole-login="true"')) {
          // Use the login keyhole if available
          stats.keyhole = processKeyholeForDisplay(loginKeyhole);
          console.log("Using login keyhole from localStorage");
        } else if (stats?.keyhole) {
          // Check if this is a downloaded SVG (not a keyhole)
          const isDownloadedSVG = stats.keyhole.includes('data-downloaded="true"');
          
          if (isDownloadedSVG) {
            // If it's a downloaded SVG, clear it to show generate button
            stats.keyhole = null;
            console.log("Detected downloaded SVG, clearing to show generate button");
          } else {
            // Otherwise process it normally
            stats.keyhole = processKeyholeForDisplay(stats.keyhole);
          }
        }
        
        userStats.value = stats;
        renderCharts();
      } catch (error) {
        console.error('Failed to load user stats:', error);
        router.push('/login');
      }
    });

    return {
      userStats,
      keyholeMessage,
      maxRatings,
      maxDownloads,
      levelProgress,
      ratingsToNextLevel,
      ratingSlider,
      downloadSlider,
      ratingsChart,
      downloadsChart,
      generateKeyhole,
      downloadKeyhole,
      logout,
      isGenerating,
      isDownloaded,
      isEmailLogin,
      buttonText,
      downloadButtonText
    }
  }
}
</script>

<style scoped>
/* Inner wrapper to center the SVG properly */
.keyhole-wrapper {
  height: max(10vh, 250px);
  width: max(10vh, 250px);
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

/* SVG element sizing and centering */
.keyhole-wrapper :deep(svg) {
  height: 100%;
  width: 100%;
  overflow: visible;
}

/* Button styles */
.action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.slider-container {
  width: 100%;
  margin: 5px 0;
}

.slider-track {
  position: relative;
  height: 20px;
  background: transparent;
  border: 1px solid #000;
  outline: none;
  margin-left: -10px;
  margin-right: -10px;
  margin: 0 20px;
  width: min(100%, 450px);
}

.slider-fill {
  height: 100%;
  background-color: black;
  position: absolute;
  top: 0;
}

.slider-thumb {
  position: absolute;
  background-color: #000;
  top: -5px;
  -webkit-appearance: none;
  width: 1px;
  background: #000;
  cursor: pointer;
  height: 30px; /* Increased height for touch area */
}

.account-links {
  position: fixed;
  bottom: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.account-links a {
  font-size: 0.75rem;
  color: #999;
  text-decoration: none;
}
</style>